import {
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  Select,
  Text,
} from '@chakra-ui/react';
import { FC, useCallback, useMemo, useState } from 'react';
import { ProviderSearchSelect } from '../../../components/provider-search';
import { ResourceProviderSearchSelect } from '../../../components/resource-provider-search/ResourceProviderSearchSelect';
import { CustomFieldValue, Practice } from '../../../types';

interface MedicareWellnessSelectProps {
  field?: CustomFieldValue;
  onChange: (updatedField: CustomFieldValue) => void;
  practices?: Practice[];
  providerBridge?: Array<{
    practiceId: string;
    metadata?: {
      'Athena-Department'?: string;
      'Athena-Link'?: string;
    };
  }>;
}

const OPTIONS = [
  { label: 'PCP Only', value: 'PCP Only' },
  {
    label: 'May cross-schedule with',
    value: 'May cross-schedule with',
  },
  { label: 'Only schedule with', value: 'Only schedule with' },
  { label: 'Resource Providers', value: 'Resource Providers' },
];

export const MedicareWellnessSelect: FC<
  MedicareWellnessSelectProps
> = ({ field, onChange, practices, providerBridge }) => {
  // Ensure we have a valid field object
  const normalizedField = useMemo(() => {
    if (!field) {
      return {
        value: '',
        metadata: {},
        id: undefined,
        fieldId: undefined,
        rowId: null,
        startDate: null,
        endDate: null,
      };
    }

    // If field.value is a string, it means the form is storing just the string value
    // We need to construct a proper field object
    if (typeof field.value === 'string') {
      return {
        value: field.value,
        metadata: field.metadata || {},
        id: field.id,
        fieldId: field.fieldId || undefined,
        rowId: field.rowId,
        startDate: field.startDate,
        endDate: field.endDate,
      };
    }

    return field;
  }, [field]);

  const value = normalizedField.value ?? '';

  // Memoize metadata to prevent unnecessary re-renders
  const metadata = useMemo(() => {
    const meta: {
      [key: string]:
        | string
        | string[]
        | boolean
        | undefined
        | { providerId: string; providerLabel: string }[]
        | { practiceId: string; practiceLabel: string; location?: string }[] // Linked clinics
        | { id: string; label: string; npi?: string; specialty?: string }[] // Old array format
        | { id: string; label: string; npi?: string; specialty?: string; tablespace: string } | null // Single format (backward compatibility)
        | { // New department-based format
            department: string;
            departmentLink?: string;
            practiceId: string;
            resourceProvider: {
              id: string;
              label: string;
              npi?: string;
              specialty?: string;
              tablespace: string;
            };
          }[] // resourceProvidersByDepartment array
        | Record<string, unknown>;
    } = normalizedField?.metadata ?? {};
    return meta;
  }, [normalizedField?.metadata]);

  // Memoize current providers to prevent unnecessary re-renders
  const currentProviders = useMemo(() => {
    return Array.isArray(metadata.providers) &&
      metadata.providers.every(
        (p) =>
          typeof p === 'object' &&
          p !== null &&
          'providerId' in p &&
          'providerLabel' in p
      )
      ? (
          metadata.providers as {
            providerId: string;
            providerLabel: string;
          }[]
        ).map((p) => ({
          id: p.providerId,
          label: p.providerLabel,
          specialty: '',
          practice: '',
        }))
      : [];
  }, [metadata.providers]);

  // Memoize current resource providers by department - support new department-based format and backward compatibility
  const currentResourceProvidersByDepartment = useMemo(() => {
    // Check for new department-based format first
    if (
      Array.isArray(metadata.resourceProvidersByDepartment) &&
      metadata.resourceProvidersByDepartment.length > 0
    ) {
      return metadata.resourceProvidersByDepartment as Array<{
        department: string;
        departmentLink?: string;
        practiceId: string;
        resourceProvider: {
          id: string;
          label: string;
          npi?: string;
          specialty?: string;
          tablespace: string;
        };
      }>;
    }

    // Fallback: Check for single resourceProvider format (backward compatibility)
    if (metadata.resourceProvider) {
      const provider = metadata.resourceProvider as {
        id: string;
        label: string;
        npi?: string;
        specialty?: string;
        tablespace: string;
      };
      if (
        typeof provider === 'object' &&
        provider !== null &&
        'id' in provider &&
        'label' in provider &&
        'tablespace' in provider
      ) {
        // Convert single format to department-based format for display
        // We'll use the first available department or a default
        const firstBridge = providerBridge?.[0];
        const department = firstBridge?.metadata?.['Athena-Department'] || 'Default Department';
        const departmentLink = firstBridge?.metadata?.['Athena-Link'];
        const practiceId = firstBridge?.practiceId || '';

        return [
          {
            department,
            departmentLink,
            practiceId,
            resourceProvider: provider,
          },
        ];
      }
    }

    // Fallback to old array format for backward compatibility
    if (
      Array.isArray(metadata.resourceProviders) &&
      metadata.resourceProviders.length > 0
    ) {
      const firstProvider = metadata.resourceProviders[0] as {
        id: string;
        label: string;
        npi?: string;
        specialty?: string;
      };
      // Try to extract tablespace from id if it's in format "tablespace-providerId"
      const idParts = firstProvider.id.split('-');
      const tablespace = idParts.length > 1 ? idParts[0] : '';
      const firstBridge = providerBridge?.[0];
      const department = firstBridge?.metadata?.['Athena-Department'] || 'Default Department';
      const departmentLink = firstBridge?.metadata?.['Athena-Link'];
      const practiceId = firstBridge?.practiceId || '';

      return [
        {
          department,
          departmentLink,
          practiceId,
          resourceProvider: {
            ...firstProvider,
            tablespace,
          },
        },
      ];
    }

    return [];
  }, [metadata.resourceProvider, metadata.resourceProviders, metadata.resourceProvidersByDepartment, providerBridge]);

  // Extract unique departments from providerBridge
  const availableDepartments = useMemo(() => {
    if (!providerBridge || providerBridge.length === 0) {
      return [];
    }

    const departments: Array<{
      department: string;
      departmentLink?: string;
      practiceId: string;
      practiceName?: string;
      tablespace: string;
    }> = [];

    providerBridge.forEach((bridge) => {
      const practice = practices?.find((p) => p.id === bridge.practiceId);
      const departmentName = bridge.metadata?.['Athena-Department'];
      const departmentLink = bridge.metadata?.['Athena-Link'];

      // Get tablespace for this practice
      const practiceWithTablespace = practice as unknown as {
        externalTablespace?: string;
      };
      let tablespace = practiceWithTablespace.externalTablespace || '';

      // Fallback to market data if externalTablespace not available
      if (!tablespace) {
        try {
          const statesData = JSON.parse(
            localStorage.getItem('statesData') || '[]'
          );
          const practiceMarketId = practice?.marketId || (practice as unknown as { marketid?: string }).marketid;

          if (practiceMarketId) {
            for (const state of statesData) {
              const market = state.markets?.find(
                (m: { id: string; tablespace?: string }) => m.id === practiceMarketId
              );
              if (market?.tablespace && market.tablespace.trim() !== '') {
                tablespace = market.tablespace.trim();
                break;
              }
            }
          }
        } catch (error) {
          console.warn('Failed to get tablespace from market data:', error);
        }
      }

      // Only include if we have a department name and tablespace
      if (departmentName && departmentName.trim() !== '' && departmentName !== '-' && tablespace) {
        // Check if this department already exists (same department name and practice)
        const exists = departments.some(
          (d) => d.department === departmentName.trim() && d.practiceId === bridge.practiceId
        );

        if (!exists) {
          departments.push({
            department: departmentName.trim(),
            departmentLink: departmentLink && departmentLink.trim() !== '' && departmentLink !== '-' && departmentLink !== 'null' && departmentLink !== 'undefined'
              ? departmentLink.trim()
              : undefined,
            practiceId: bridge.practiceId,
            practiceName: practice?.name,
            tablespace,
          });
        }
      }
    });

    return departments;
  }, [practices, providerBridge]);

  // Local state for checkbox to enable provider search
  const [enableProviderSearch, setEnableProviderSearch] =
    useState<boolean>(currentProviders.length > 0);

  // Show provider select when checkbox is checked and a value is selected (but not for Resource Providers)
  const showProviderSelect =
    enableProviderSearch &&
    value &&
    value !== '' &&
    value !== 'Resource Providers';

  // Show resource provider select when Resource Providers is selected
  const showResourceProviderSelect = value === 'Resource Providers';

  // Update providers in metadata
  const updateProviders = useCallback(
    (
      providers: {
        id: string;
        label: string;
        specialty?: string;
        practice?: string;
      }[]
    ) => {
      const newMetadata = {
        ...metadata,
        providers: providers.map((p) => ({
          providerId: p.id,
          providerLabel: p.label,
        })),
      };
      onChange({
        value: normalizedField?.value ?? '',
        fieldId: normalizedField?.fieldId ?? '',
        rowId: normalizedField?.rowId ?? null,
        id: normalizedField?.id ?? undefined,
        startDate: normalizedField?.startDate ?? null,
        endDate: normalizedField?.endDate ?? null,
        metadata: newMetadata,
      });
    },
    [metadata, normalizedField, onChange]
  );

  // Update resource provider for a specific department
  const updateResourceProviderForDepartment = useCallback(
    (
      department: string,
      departmentLink: string | undefined,
      practiceId: string,
      resourceProvider: {
        id: string;
        label: string;
        npi?: string;
        specialty?: string;
        tablespace: string;
      } | null
    ) => {
      const deptBased = metadata.resourceProvidersByDepartment;
      const currentByDept = Array.isArray(deptBased) &&
        deptBased.length > 0 &&
        deptBased.every(
          (item): item is {
            department: string;
            departmentLink?: string;
            practiceId: string;
            resourceProvider: {
              id: string;
              label: string;
              npi?: string;
              specialty?: string;
              tablespace: string;
            };
          } =>
            typeof item === 'object' &&
            item !== null &&
            'department' in item &&
            'practiceId' in item &&
            'resourceProvider' in item
        )
        ? deptBased
        : [];

      let updatedByDept: typeof currentByDept;

      if (resourceProvider === null) {
        // Remove the entry for this department
        updatedByDept = currentByDept.filter(
          (item) => !(item.department === department && item.practiceId === practiceId)
        );
      } else {
        // Update or add entry for this department
        const existingIndex = currentByDept.findIndex(
          (item) => item.department === department && item.practiceId === practiceId
        );

        const newEntry = {
          department,
          departmentLink,
          practiceId,
          resourceProvider: {
            id: resourceProvider.id,
            label: resourceProvider.label,
            npi: resourceProvider.npi,
            specialty: resourceProvider.specialty,
            tablespace: resourceProvider.tablespace,
          },
        };

        if (existingIndex >= 0) {
          updatedByDept = [...currentByDept];
          updatedByDept[existingIndex] = newEntry;
        } else {
          updatedByDept = [...currentByDept, newEntry];
        }
      }

      const newMetadata = {
        ...metadata,
        resourceProvidersByDepartment: updatedByDept.length > 0 ? updatedByDept : undefined,
        // Keep backward compatibility: if only one department, also set resourceProvider
        resourceProvider: updatedByDept.length === 1 ? updatedByDept[0].resourceProvider : undefined,
        // Remove old array format if it exists
        resourceProviders: undefined,
      };

      onChange({
        value: normalizedField?.value ?? '',
        fieldId: normalizedField?.fieldId ?? '',
        rowId: normalizedField?.rowId ?? null,
        id: normalizedField?.id ?? undefined,
        startDate: normalizedField?.startDate ?? null,
        endDate: normalizedField?.endDate ?? null,
        metadata: newMetadata,
      });
    },
    [metadata, normalizedField, onChange]
  );


  const handleDropdownChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = e.target.value;
    let newMetadata: typeof metadata = { ...metadata };
    if (newValue === 'PCP Only') {
      newMetadata = {}; // Empty object
    } else if (newValue === 'Resource Providers') {
      // Keep existing metadata but ensure resourceProvider exists
      newMetadata = {
        ...metadata,
        resourceProvider: (metadata.resourceProvider as { id: string; label: string; npi?: string; specialty?: string; tablespace: string } | null) || null,
        resourceProviders: undefined, // Remove old array format
      };
    }
    onChange({
      value: newValue ?? '',
      fieldId: normalizedField?.fieldId ?? '',
      rowId: normalizedField?.rowId ?? null,
      id: normalizedField?.id ?? undefined,
      startDate: normalizedField?.startDate ?? null,
      endDate: normalizedField?.endDate ?? null,
      metadata: newMetadata,
    });
  };

  // Handle checkbox toggle for provider search
  const handleProviderSearchToggle = useCallback(
    (isChecked: boolean) => {
      setEnableProviderSearch(isChecked);
      if (!isChecked) {
        // Clear providers when unchecking
        updateProviders([]);
      }
    },
    [updateProviders]
  );

  return (
    <FormControl mb="1.5rem">
      <FormLabel>Medicare Wellness Scheduling</FormLabel>
      <Select
        value={
          OPTIONS.some((opt) => opt.value === value) ? value : ''
        }
        placeholder="Select option"
        onChange={handleDropdownChange}
        width="100%"
      >
        {OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      {/* Optional Provider Search Checkbox - Only show for non-Resource Providers options */}
      {value &&
        value !== '' &&
        value !== 'Resource Providers' && (
          <Box
            width="100%"
            p="1rem"
            bg="blue.50"
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
            mt="1rem"
          >
            <Checkbox
              isChecked={enableProviderSearch}
              onChange={(e) =>
                handleProviderSearchToggle(e.target.checked)
              }
              colorScheme="blue"
              size="md"
            >
              <Text fontSize="sm" fontWeight="500" color="blue.700">
                Also search and select specific providers to link?
              </Text>
            </Checkbox>
            <Text fontSize="0.75rem" color="blue.600" mt={1} ml={6}>
              This will allow you to link to actual provider profiles in
              addition to the selected option
            </Text>
          </Box>
        )}

      {/* Provider Search Section - Show only when checkbox is checked */}
      <Collapse in={Boolean(showProviderSelect)} animateOpacity>
        <Box
          mt={4}
          width="100%"
          p="1.5rem"
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          _hover={{
            borderColor: 'gray.300',
            boxShadow: 'sm',
          }}
          transition="all 0.2s"
        >
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.700"
            mb="1rem"
          >
            Search and Select Providers
          </Text>

          <ProviderSearchSelect
            selectedProviders={currentProviders}
            onProvidersChange={updateProviders}
            placeholder="Search for providers"
          />
        </Box>
      </Collapse>

      {/* Resource Provider Selection Section - Show when Resource Providers is selected */}
      {/* IMPORTANT: Only render ResourceProviderSearchSelect components when Resource Providers is selected */}
      {/* This prevents unnecessary API calls when user is only using Department Autofill Helper */}
      {showResourceProviderSelect && (
        <Collapse in={true} animateOpacity>
          <Box mt={4} width="100%">
            {availableDepartments.length === 0 ? (
              <Box
                p="1rem"
                bg="yellow.50"
                borderRadius="md"
                border="1px solid"
                borderColor="yellow.200"
              >
                <Text fontSize="sm" color="yellow.700" fontWeight="500" mb="0.5rem">
                  No Departments Found
                </Text>
                <Text fontSize="xs" color="yellow.600">
                  Please add a practice in the "Practice Details" section below and ensure it has Athena Department information. The department will be used to organize resource provider selection.
                </Text>
              </Box>
            ) : (
              <Box>
                <FormLabel fontSize="sm" fontWeight="500" color="gray.700" mb="1rem">
                  Select Resource Provider by Department
                </FormLabel>
                <Text fontSize="xs" color="gray.600" mb="1rem">
                  You can assign different resource providers for different departments within the same tablespace.
                </Text>
                {availableDepartments.map((dept) => {
                  const existingEntry = currentResourceProvidersByDepartment.find(
                    (item) => item.department === dept.department && item.practiceId === dept.practiceId
                  );
                  const selectedProvider = existingEntry?.resourceProvider || null;

                  return (
                    <Box
                      key={`${dept.practiceId}-${dept.department}`}
                      mb="1.5rem"
                      p="1rem"
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb="0.5rem">
                        {dept.department}
                        {dept.practiceName && (
                          <Text as="span" fontSize="xs" fontWeight="400" color="gray.500" ml="0.5rem">
                            ({dept.practiceName})
                          </Text>
                        )}
                      </FormLabel>
                      <ResourceProviderSearchSelect
                        selectedProvider={selectedProvider}
                        onProviderChange={(provider) =>
                          updateResourceProviderForDepartment(
                            dept.department,
                            dept.departmentLink,
                            dept.practiceId,
                            provider
                          )
                        }
                        tablespaces={[dept.tablespace]}
                        placeholder={`Select resource provider for ${dept.department}`}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </FormControl>
  );
};
